import { ratiosFromAngle } from './ratios';
import { formatRadian } from './angles';

export interface WaveParams {
  fn: 'sin' | 'cos' | 'tan' | 'csc' | 'sec' | 'cot';
  A: number;
  B: number;
  C: number;
  D: number;
}

export function evaluateWave(p: WaveParams, x: number): number {
  const theta = p.B * (x - p.C);
  const r = ratiosFromAngle(theta);
  return p.A * r[p.fn] + p.D;
}

export function rForm(a: number, b: number): { R: number; alpha: number } {
  const R = Math.hypot(a, b);
  const alpha = Math.atan2(b, a);
  return { R, alpha };
}

/** Three decimals, trailing zeros trimmed, U+2212 for negatives; "-0" never leaks. */
function fmtNum(v: number): string {
  const abs = String(Number(Math.abs(v).toFixed(3)));
  return v < 0 && abs !== '0' ? `−${abs}` : abs;
}

/** Period: 2π/|B| for sin/cos/csc/sec, π/|B| for tan/cot; Infinity when B = 0. */
export function wavePeriod(p: WaveParams): number {
  const full = p.fn === 'tan' || p.fn === 'cot' ? Math.PI : 2 * Math.PI;
  return p.B === 0 ? Infinity : full / Math.abs(p.B);
}

/** "y = A·fn(B(x − C)) + D" with unit coefficients and zero terms dropped. */
export function formatWaveFormula(p: WaveParams): string {
  const coeff = p.A === 1 ? '' : p.A === -1 ? '−' : `${fmtNum(p.A)}·`;
  const shiftX = p.C === 0 ? '' : p.C > 0 ? ` − ${formatRadian(p.C)}` : ` + ${formatRadian(-p.C)}`;
  const inner =
    p.B === 1 ? `x${shiftX}` : p.C === 0 ? `${fmtNum(p.B)}x` : `${fmtNum(p.B)}(x${shiftX})`;
  const shiftY = p.D === 0 ? '' : p.D > 0 ? ` + ${fmtNum(p.D)}` : ` − ${fmtNum(-p.D)}`;
  return `y = ${coeff}${p.fn}(${inner})${shiftY}`;
}

/** "y = R·sin(x + α)" for a sin x + b cos x, e.g. "y = 1.414·sin(x + π/4)". */
export function formatRFormFormula(a: number, b: number): string {
  const { R, alpha } = rForm(a, b);
  const shift =
    alpha === 0 ? '' : alpha > 0 ? ` + ${formatRadian(alpha)}` : ` − ${formatRadian(-alpha)}`;
  return `y = ${fmtNum(R)}·sin(x${shift})`;
}

export interface WaveAnnotations {
  /** y = D. */
  midline: number;
  /** [D − |A|, D + |A|]; null for tan / cot. */
  envelope: [number, number] | null;
  /** Vertical bracket from the midline to the first extremum at/after C; null for tan / cot. */
  amplitude: { x: number; y0: number; y1: number } | null;
  /** One period from x = C. */
  period: { x0: number; x1: number; label: string };
  /** Horizontal arrow from x = 0 to x = C on the midline; null when C = 0. */
  phase: { x0: 0; x1: number; y: number } | null;
  /** Vertical arrow from y = 0 to y = D at x = C; null when D = 0. */
  shift: { x: number; y0: 0; y1: number } | null;
}

/** Pure geometry for the on-chart parameter annotations. */
export function waveAnnotations(p: WaveParams): WaveAnnotations {
  const T = wavePeriod(p);
  const bounded = p.fn !== 'tan' && p.fn !== 'cot';
  const amp = Math.abs(p.A);
  const peakX = p.fn === 'sin' || p.fn === 'csc' ? p.C + T / 4 : p.C;
  return {
    midline: p.D,
    envelope: bounded ? [p.D - amp, p.D + amp] : null,
    amplitude: bounded ? { x: peakX, y0: p.D, y1: p.D + p.A } : null,
    period: { x0: p.C, x1: p.C + T, label: `T = ${formatRadian(T)}` },
    phase: p.C === 0 ? null : { x0: 0, x1: p.C, y: p.D },
    shift: p.D === 0 ? null : { x: p.C, y0: 0, y1: p.D },
  };
}
